import React from 'react';
import { ArrowRight, Sparkles, BookOpen, Users } from 'lucide-react';
import { trackUpsellClick } from '../utils/analytics';

interface UpsellBannerProps {
  moduleId: number;
  type: 'advanced' | 'masterclass' | 'consulting';
  title: string;
  description: string;
  ctaText: string;
  ctaLink?: string;
  features?: string[];
  userId?: string;
  onCTAClick?: () => void;
  onWaitingListClick?: () => void;
}

export function UpsellBanner({
  moduleId,
  type,
  title,
  description,
  ctaText,
  ctaLink,
  features,
  userId,
  onCTAClick,
  onWaitingListClick
}: UpsellBannerProps) {
  const handleClick = () => {
    trackUpsellClick({ moduleId, upsellType: type, userId });
    
    // For "advanced" type, trigger waiting list modal
    if (type === 'advanced' && onWaitingListClick) {
      onWaitingListClick();
      return;
    }
    
    if (onCTAClick) {
      onCTAClick();
    } else if (ctaLink && ctaLink !== '#') {
      window.open(ctaLink, '_blank');
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'advanced':
        return <Sparkles style={{ width: '24px', height: '24px', color: 'var(--orange)' }} />;
      case 'masterclass':
        return <BookOpen style={{ width: '24px', height: '24px', color: 'var(--orange)' }} />;
      case 'consulting':
        return <Users style={{ width: '24px', height: '24px', color: 'var(--orange)' }} />;
      default:
        return null;
    }
  };

  return (
    <div className="upsell-banner" style={{
      background: 'white',
      borderRadius: '12px',
      padding: '1.5rem',
      marginTop: '2rem',
      border: '1px solid #e5e7eb',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    }}>
      <div className="upsell-banner-header" style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        marginBottom: features ? '1rem' : 0
      }}>
        <div className="upsell-banner-icon" style={{
          background: 'white',
          borderRadius: '8px',
          padding: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          {getIcon()}
        </div>
        <div className="upsell-banner-content" style={{ flex: 1 }}>
          <h3 className="upsell-banner-title" style={{
            margin: '0 0 1rem 0',
            fontSize: '1.25rem',
            fontWeight: '700',
            color: 'var(--dark-gray)'
          }}>
            {title}
          </h3>
          <p style={{
            margin: '0 0 1rem 0',
            fontSize: '0.95rem',
            color: 'var(--gray)',
            lineHeight: '1.6'
          }}>
            {description}
          </p>
        </div>
      </div>

      {features && features.length > 0 && (
        <ul className="upsell-banner-features" style={{
          margin: '0 0 1rem 0',
          paddingLeft: '1.5rem',
          paddingRight: '1rem',
          listStyle: 'none',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {features.map((feature, index) => (
            <li key={index} className="upsell-feature-item" style={{
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--gray)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem'
            }}>
              <span className="upsell-checkmark" style={{ color: 'var(--orange)', flexShrink: 0 }}>✓</span>
              <span className="upsell-feature-text">{feature}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        className="upsell-banner-button"
        onClick={handleClick}
        style={{
          background: 'var(--orange)',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          padding: '0.75rem 1.5rem',
          fontSize: '0.95rem',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s ease'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#e68900';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'var(--orange)';
        }}
      >
        {ctaText}
        <ArrowRight style={{ width: '18px', height: '18px' }} />
      </button>
    </div>
  );
}

